import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import { FaWhatsapp, FaWeixin } from "react-icons/fa";
import { MapPin, Phone, Clock, ArrowUpRight, X, Copy, Check, UserRound } from "lucide-react";
import { translations, CONTACT, IMAGES } from "@/content";
import { trackEvent } from "@/analytics";
import { Overline, SerifHeading, FadeUp, RevealLines, EASE } from "@/primitives";
import { HowItWorks, FAQ, MapEmbed, StickyMobileBar, BackToTop } from "@/extras";
import { useAuth } from "@/auth/AuthContext";
import { Link } from "react-router-dom";
import "@/App.css";

const Brand = ({ className = "" }) => (
  <span className={className}>
    Nunu <em className="italic text-matcha">Massage</em>
  </span>
);

/* ------------------------------- Header -------------------------------- */

const PromoBar = ({ t, hidden, onClose }) => (
  <AnimatePresence>
    {!hidden && (
      <motion.div
        data-testid="promo-bar"
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -40, opacity: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-0 left-0 right-0 z-50 bg-ink text-sand"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-2.5 flex items-center justify-between gap-4 text-[12px]">
          <div className="flex items-center gap-3 min-w-0">
            <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 border border-clay/40 text-clay text-[10px] tracking-[0.22em] uppercase shrink-0">
              {t.promo.tag}
            </span>
            <span className="truncate text-sand/90">{t.promo.text}</span>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <a
              href={CONTACT.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="promo-whatsapp"
              onClick={() => trackEvent("promo_claim", { channel: "whatsapp", source: "promo_bar" })}
              className="inline-flex items-center gap-2 text-whatsapp hover:text-white transition-colors text-[11px] tracking-[0.22em] uppercase"
            >
              <FaWhatsapp className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{t.promo.cta}</span>
            </a>
            <button
              type="button"
              onClick={onClose}
              data-testid="promo-close"
              aria-label="Dismiss"
              className="text-sand/60 hover:text-sand transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

const AuthButton = ({ t }) => {
  const { user, loading, login } = useAuth();
  if (loading) return null;
  if (user) {
    return (
      <Link
        to="/account"
        data-testid="header-account-link"
        aria-label={t.account.myAccount}
        className="flex items-center gap-2 group"
      >
        {user.picture ? (
          <img
            src={user.picture}
            alt=""
            referrerPolicy="no-referrer"
            className="w-9 h-9 rounded-full object-cover ring-1 ring-clay group-hover:ring-matcha transition-shadow"
          />
        ) : (
          <span className="w-9 h-9 rounded-full bg-matcha text-sand flex items-center justify-center font-serif-display text-lg">
            {user.name.charAt(0)}
          </span>
        )}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        trackEvent("sign_in_click", { source: "header" });
        login();
      }}
      data-testid="header-sign-in"
      className="inline-flex items-center gap-2 text-[12px] font-sans-body text-ink-soft hover:text-matcha transition-colors"
    >
      <UserRound className="w-4 h-4" />
      <span className="hidden sm:inline">{t.account.signIn}</span>
    </button>
  );
};

const Header = ({ lang, setLang, t, offset = 0 }) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      style={{ top: `${offset}px` }}
      className={`fixed left-0 right-0 z-40 transition-all duration-500 ${
        scrolled
          ? "bg-sand/80 backdrop-blur-xl border-b border-clay/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
        <a href="#top" data-testid="logo" className="flex items-center gap-2 group">
          <span className="w-2 h-2 bg-matcha rounded-full" />
          <span className="font-serif-display text-xl md:text-[22px] tracking-tight">
            <Brand />
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-9 text-[13px] font-sans-body text-ink-soft">
          <a href="#services" data-testid="nav-services" className="hover:text-matcha transition-colors">
            {t.nav.services}
          </a>
          <a href="#about" data-testid="nav-about" className="hover:text-matcha transition-colors">
            {t.nav.about}
          </a>
          <a href="#location" data-testid="nav-location" className="hover:text-matcha transition-colors">
            {t.nav.location}
          </a>
          <a href="#contact" data-testid="nav-contact" className="hover:text-matcha transition-colors">
            {t.nav.contact}
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <AuthButton t={t} />
          <div
            data-testid="lang-toggle"
            className="flex items-center text-[12px] font-sans-body border border-clay/80 rounded-full overflow-hidden"
          >
            <button
              data-testid="lang-toggle-en"
              onClick={() => setLang("en")}
              className={`px-3 py-1.5 transition-colors ${
                lang === "en" ? "bg-ink text-sand" : "text-ink-soft hover:text-ink"
              }`}
            >
              EN
            </button>
            <button
              data-testid="lang-toggle-zh"
              onClick={() => setLang("zh")}
              className={`px-3 py-1.5 transition-colors ${
                lang === "zh" ? "bg-ink text-sand" : "text-ink-soft hover:text-ink"
              }`}
            >
              中文
            </button>
          </div>
          <a
            href={CONTACT.whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-book-now"
            onClick={() => trackEvent("booking_intent", { channel: "whatsapp", source: "header" })}
            className="hidden sm:inline-flex items-center gap-2 bg-ink text-sand px-5 py-2.5 text-[12px] tracking-[0.18em] uppercase hover:bg-matcha transition-colors duration-500"
          >
            {t.nav.bookNow}
            <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </header>
  );
};

/* -------------------------------- Hero --------------------------------- */

const Hero = ({ t }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const badgeY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative pt-36 md:pt-44 pb-20 md:pb-28 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-14 items-end">
        <motion.div style={{ y: textY }} className="md:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
          >
            <Overline>— {t.hero.overline}</Overline>
          </motion.div>
          <SerifHeading as="h1" className="mt-6 text-[56px] sm:text-[88px] md:text-[120px]">
            <RevealLines delay={0.25} lines={[t.hero.titleA]} />
            <RevealLines
              delay={0.4}
              lines={[t.hero.titleB]}
              className="italic text-matcha font-normal"
            />
          </SerifHeading>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.75 }}
            className="mt-8 max-w-lg text-ink-soft text-[15px] md:text-base leading-relaxed"
          >
            {t.hero.subtitle}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={CONTACT.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta"
              onClick={() => trackEvent("booking_intent", { channel: "whatsapp", source: "hero" })}
              className="group inline-flex items-center gap-3 bg-ink text-sand px-7 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-matcha transition-all duration-500"
            >
              <FaWhatsapp className="w-4 h-4" />
              {t.hero.cta}
              <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#contact"
              data-testid="hero-wechat-link"
              className="inline-flex items-center gap-2 text-ink-soft text-[12px] tracking-[0.22em] uppercase border-b border-ink-soft/40 pb-1 hover:text-matcha hover:border-matcha transition-colors"
            >
              <FaWeixin className="w-4 h-4" />
              {t.hero.ctaAlt}
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.1 }}
            className="mt-10 flex items-center gap-3 text-[12px] text-ink-soft font-sans-body"
          >
            <Clock className="w-3.5 h-3.5" />
            <span>{t.hero.meta}</span>
          </motion.div>
        </motion.div>

        <div className="md:col-span-5 relative">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.4, ease: EASE, delay: 0.3 }}
            className="relative aspect-[3/4] overflow-hidden"
          >
            <motion.img
              style={{ y: imgY, scale: imgScale }}
              src={IMAGES.hero}
              alt="Elegant portrait — Nunu Massage"
              className="w-full h-[115%] -mt-[7%] object-cover will-change-transform"
              loading="eager"
              fetchPriority="high"
            />
            <div className="absolute inset-0 ring-1 ring-clay/40 pointer-events-none" />
          </motion.div>
          <motion.div
            style={{ y: badgeY }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.2 }}
            className="absolute -bottom-6 -left-6 md:-left-10 bg-sand border border-clay/80 px-6 py-4"
          >
            <div className="font-serif-display italic text-matcha text-2xl">Nunu</div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-ink-soft mt-1">
              Est. Shanghai
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------ Marquee -------------------------------- */

const Marquee = ({ items }) => {
  const doubled = [...items, ...items, ...items, ...items];
  return (
    <section aria-hidden="true" className="border-y border-clay/60 py-5 bg-sand-deep overflow-hidden">
      <div className="flex marquee-track gap-12 whitespace-nowrap">
        {doubled.map((w, i) => (
          <span
            key={i}
            className="font-serif-display italic text-2xl md:text-3xl text-ink-soft/80"
          >
            {w}
            <span className="ml-12 text-matcha">✦</span>
          </span>
        ))}
      </div>
    </section>
  );
};

/* -------------------------------- About -------------------------------- */

const About = ({ t }) => (
  <section id="about" data-testid="about-section" className="py-24 md:py-36">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 md:gap-20">
      <div className="md:col-span-5 relative">
        <FadeUp>
          <div className="relative aspect-[4/5] overflow-hidden">
            <img
              src={IMAGES.portrait}
              alt="Nunu Massage hospitality"
              className="w-full h-full object-cover grayscale-[10%] transition-transform duration-[1400ms] hover:scale-105"
              loading="lazy"
            />
          </div>
        </FadeUp>
      </div>
      <div className="md:col-span-7 md:pt-10">
        <FadeUp>
          <Overline>— {t.about.overline}</Overline>
        </FadeUp>
        <FadeUp delay={0.1}>
          <SerifHeading className="mt-6 text-[40px] md:text-[64px] whitespace-pre-line">
            {t.about.title}
          </SerifHeading>
        </FadeUp>
        <FadeUp delay={0.2}>
          <div className="mt-8 max-w-xl space-y-5 text-ink-soft text-[15px] leading-relaxed">
            <p>{t.about.p1}</p>
            <p>{t.about.p2}</p>
          </div>
        </FadeUp>
        <FadeUp delay={0.3}>
          <div className="mt-12 grid grid-cols-3 gap-6 md:gap-10 max-w-lg border-t border-clay/70 pt-8">
            {t.about.stats.map((s, i) => (
              <div key={i} data-testid={`about-stat-${i}`}>
                <div className="font-serif-display text-3xl md:text-4xl text-matcha">
                  {s.k}
                </div>
                <div className="mt-1 text-[11px] tracking-[0.18em] uppercase text-ink-soft">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </FadeUp>
      </div>
    </div>
  </section>
);

/* ------------------------------ Services ------------------------------- */

const Services = ({ t, onBook }) => (
  <motion.section
    id="services"
    data-testid="services-section"
    onViewportEnter={() => trackEvent("view_pricing", { source: "services" })}
    viewport={{ once: true, margin: "-200px" }}
    className="py-24 md:py-36 bg-sand-deep relative overflow-hidden"
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <FadeUp>
            <Overline>— {t.services.overline}</Overline>
          </FadeUp>
          <FadeUp delay={0.1}>
            <SerifHeading className="mt-6 text-[44px] md:text-[72px]">
              {t.services.title}
            </SerifHeading>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-6 text-ink-soft text-[15px] leading-relaxed max-w-sm">
              {t.services.subtitle}
            </p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <div className="mt-8 text-[11px] tracking-[0.22em] uppercase text-ink-soft">
              {t.services.note}
            </div>
          </FadeUp>
        </div>

        <div className="md:col-span-7 md:pl-8">
          <ul className="divide-y divide-clay/70 border-t border-b border-clay/70">
            {t.services.tiers.map((tier, i) => (
              <motion.li
                key={i}
                data-testid={`service-tier-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="group py-8 md:py-10 flex flex-col md:flex-row md:items-center gap-4 md:gap-8"
              >
                <div className="md:w-1/4">
                  <div className="text-[11px] tracking-[0.22em] uppercase text-matcha">
                    {tier.zh}
                  </div>
                  <div className="font-serif-display text-3xl md:text-4xl mt-1">
                    {tier.name}
                  </div>
                </div>
                <div className="md:w-1/3 text-ink-soft text-[14px] leading-relaxed">
                  {tier.desc}
                </div>
                <div className="md:w-1/6 font-sans-body text-[13px] tracking-[0.18em] uppercase text-ink-soft">
                  {tier.duration}
                </div>
                <div className="md:w-1/6 font-serif-display text-3xl md:text-4xl text-ink">
                  {tier.price}
                </div>
                <div className="md:w-auto md:ml-auto">
                  <a
                    href={CONTACT.whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`service-book-${i}`}
                    className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-ink group-hover:text-matcha transition-colors"
                    onClick={() => {
                      trackEvent("booking_intent", {
                        channel: "whatsapp",
                        source: "services",
                        tier: tier.duration,
                        price: tier.price,
                      });
                      onBook && onBook();
                    }}
                  >
                    {t.services.book}
                    <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
                  </a>
                </div>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </motion.section>
);

/* ---------------------------- Therapist row ---------------------------- */

const Therapist = ({ t }) => (
  <section className="py-24 md:py-32" data-testid="therapist-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-14 items-center">
      <div className="md:col-span-6 md:order-2">
        <FadeUp>
          <div className="relative aspect-[4/5] overflow-hidden">
            <img
              src={IMAGES.therapist}
              alt="Nunu Massage therapist"
              className="w-full h-full object-cover transition-transform duration-[1400ms] hover:scale-105"
              loading="lazy"
            />
          </div>
        </FadeUp>
      </div>
      <div className="md:col-span-6 md:order-1 md:pr-10">
        <FadeUp>
          <Overline>— {t.therapist.overline}</Overline>
        </FadeUp>
        <FadeUp delay={0.1}>
          <SerifHeading className="mt-6 text-[40px] md:text-[60px]">
            {t.therapist.title}
          </SerifHeading>
        </FadeUp>
        <FadeUp delay={0.2}>
          <p className="mt-8 text-ink-soft text-[15px] leading-relaxed max-w-lg">
            {t.therapist.p}
          </p>
        </FadeUp>
        <FadeUp delay={0.3}>
          <div className="mt-10 grid grid-cols-2 gap-4 max-w-md">
            <div className="aspect-square overflow-hidden">
              <img
                src={IMAGES.cultural}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="aspect-square overflow-hidden">
              <img
                src={IMAGES.interior}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </FadeUp>
      </div>
    </div>
  </section>
);

/* ---------------------------- Testimonials ----------------------------- */

const StarRow = () => (
  <div aria-label="5 out of 5 stars" className="flex items-center gap-1 text-matcha">
    {[0, 1, 2, 3, 4].map((i) => (
      <svg key={i} viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current">
        <path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
      </svg>
    ))}
  </div>
);

const Testimonials = ({ t }) => (
  <section
    data-testid="testimonials-section"
    className="py-24 md:py-32 bg-sand-deep border-y border-clay/60"
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <div className="grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-7">
          <FadeUp>
            <Overline>— {t.testimonials.overline}</Overline>
          </FadeUp>
          <FadeUp delay={0.1}>
            <SerifHeading className="mt-6 text-[40px] md:text-[64px]">
              {t.testimonials.title}
            </SerifHeading>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-6 text-ink-soft text-[15px] leading-relaxed max-w-md">
              {t.testimonials.subtitle}
            </p>
          </FadeUp>
        </div>
        <div className="md:col-span-5 flex items-center md:justify-end gap-3">
          <FadeUp delay={0.2}>
            <div className="flex items-center gap-3">
              <StarRow />
              <span className="text-[11px] tracking-[0.22em] uppercase text-ink-soft">
                {t.testimonials.meta}
              </span>
            </div>
          </FadeUp>
        </div>
      </div>

      <div className="mt-14 md:mt-20 grid md:grid-cols-2 gap-px bg-clay/60">
        {t.testimonials.items.map((item, i) => (
          <motion.figure
            key={i}
            data-testid={`testimonial-${i}`}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
            className="bg-sand-deep p-8 md:p-10 flex flex-col justify-between min-h-[280px]"
          >
            <div>
              <StarRow />
              <blockquote className="mt-6 font-serif-display text-[22px] md:text-[26px] leading-snug text-ink">
                “{item.q}”
              </blockquote>
            </div>
            <figcaption className="mt-8 flex items-baseline justify-between text-[11px] tracking-[0.22em] uppercase">
              <span className="text-ink">
                {item.name}
                <span className="text-ink-soft font-sans-body normal-case tracking-normal text-[12px] ml-2">
                  · {item.role}
                </span>
              </span>
              <span className="text-matcha">{item.src}</span>
            </figcaption>
          </motion.figure>
        ))}
      </div>
    </div>
  </section>
);

/* ------------------------------ Location ------------------------------- */

const Location = ({ t }) => (
  <section
    id="location"
    data-testid="location-section"
    className="relative py-24 md:py-36 bg-matcha text-sand overflow-hidden"
  >
    <div className="absolute inset-0 opacity-20 pointer-events-none">
      <img src={IMAGES.zen} alt="" className="w-full h-full object-cover" />
    </div>
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10 grid md:grid-cols-12 gap-10">
      <div className="md:col-span-7">
        <FadeUp>
          <span className="text-[11px] tracking-[0.28em] uppercase text-sand/70">
            — {t.location.overline}
          </span>
        </FadeUp>
        <FadeUp delay={0.1}>
          <h2 className="mt-6 font-serif-display font-light leading-[1.04] text-[44px] md:text-[76px] whitespace-pre-line tracking-tight">
            {t.location.title}
          </h2>
        </FadeUp>
        <FadeUp delay={0.2}>
          <p className="mt-8 text-sand/80 max-w-lg leading-relaxed text-[15px]">
            {t.location.sub}
          </p>
        </FadeUp>
        <FadeUp delay={0.3}>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href={CONTACT.mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="view-map-btn"
              onClick={() => trackEvent("map_open", { source: "location" })}
              className="inline-flex items-center gap-2 border border-sand/70 px-6 py-3 text-[11px] tracking-[0.22em] uppercase hover:bg-sand hover:text-matcha transition-colors duration-500"
            >
              <MapPin className="w-3.5 h-3.5" />
              {t.location.viewMap}
            </a>
            <a
              href={`tel:${CONTACT.phone}`}
              data-testid="call-studio-btn"
              onClick={() => trackEvent("phone_click", { source: "location" })}
              className="inline-flex items-center gap-2 text-sand text-[11px] tracking-[0.22em] uppercase hover:text-clay transition-colors"
            >
              <Phone className="w-3.5 h-3.5" />
              {t.location.callUs}
            </a>
          </div>
        </FadeUp>
      </div>
      <div className="md:col-span-5 md:pl-10 md:border-l md:border-sand/20">
        <FadeUp delay={0.2}>
          <div className="space-y-8">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-sand/60">
                {t.contact.addressLabel}
              </div>
              <div className="mt-3 font-serif-display text-xl md:text-2xl leading-snug">
                {CONTACT.address}
                <br />
                <span className="text-sand/70 text-lg">{CONTACT.addressZh}</span>
              </div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-sand/60">
                {t.contact.phoneLabel}
              </div>
              <div className="mt-3 font-serif-display text-xl md:text-2xl">
                {CONTACT.phoneDisplay}
              </div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-sand/60">
                {t.contact.hoursLabel}
              </div>
              <div className="mt-3 font-serif-display text-xl md:text-2xl">
                {t.contact.hours}
              </div>
            </div>
          </div>
        </FadeUp>
      </div>
    </div>
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
      <MapEmbed t={t} />
    </div>
  </section>
);

/* ------------------------------- Contact ------------------------------- */

const Contact = ({ t, openWeChat }) => (
  <section id="contact" data-testid="contact-section" className="py-24 md:py-36">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 text-center md:text-left">
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7">
          <FadeUp>
            <Overline>— {t.contact.overline}</Overline>
          </FadeUp>
          <FadeUp delay={0.1}>
            <SerifHeading className="mt-6 text-[48px] md:text-[88px]">
              {t.contact.title}
            </SerifHeading>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-6 text-ink-soft text-[15px] leading-relaxed max-w-xl">
              {t.contact.p}
            </p>
          </FadeUp>
        </div>

        <div className="md:col-span-5 md:pl-10 md:border-l md:border-clay/70 flex flex-col gap-4 md:pt-6">
          <FadeUp delay={0.15}>
            <a
              href={CONTACT.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-btn"
              onClick={() => trackEvent("whatsapp_click", { source: "contact" })}
              className="group flex items-center justify-between gap-4 bg-whatsapp text-white px-6 py-5 hover:brightness-95 transition-all"
            >
              <span className="flex items-center gap-3">
                <FaWhatsapp className="w-5 h-5" />
                <span className="text-[12px] tracking-[0.22em] uppercase">
                  {t.contact.whatsapp}
                </span>
              </span>
              <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
            </a>
          </FadeUp>
          <FadeUp delay={0.25}>
            <button
              type="button"
              onClick={() => {
                trackEvent("wechat_click", { source: "contact" });
                openWeChat();
              }}
              data-testid="contact-wechat-btn"
              className="group flex items-center justify-between gap-4 bg-wechat text-white px-6 py-5 hover:brightness-95 transition-all"
            >
              <span className="flex items-center gap-3">
                <FaWeixin className="w-5 h-5" />
                <span className="text-[12px] tracking-[0.22em] uppercase">
                  {t.contact.wechat}
                </span>
              </span>
              <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
            </button>
          </FadeUp>
          <FadeUp delay={0.35}>
            <a
              href={`tel:${CONTACT.phone}`}
              data-testid="contact-phone-btn"
              onClick={() => trackEvent("phone_click", { source: "contact" })}
              className="group flex items-center justify-between gap-4 border border-ink/80 text-ink px-6 py-5 hover:bg-ink hover:text-sand transition-all duration-500"
            >
              <span className="flex items-center gap-3">
                <Phone className="w-4 h-4" />
                <span className="text-[12px] tracking-[0.22em] uppercase">
                  {CONTACT.phoneDisplay}
                </span>
              </span>
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </FadeUp>
        </div>
      </div>

      {/* QR Scan Block */}
      <div className="mt-20 md:mt-28 border-t border-clay/70 pt-16 md:pt-20 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <FadeUp>
            <Overline>— QR · 二维码</Overline>
          </FadeUp>
          <FadeUp delay={0.1}>
            <SerifHeading className="mt-6 text-[36px] md:text-[52px]">
              {t.contact.qrTitle}
            </SerifHeading>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-5 text-ink-soft text-[14px] leading-relaxed max-w-sm">
              {t.contact.qrSub}
            </p>
          </FadeUp>
        </div>
        <div className="md:col-span-8 grid sm:grid-cols-2 gap-6">
          <FadeUp delay={0.2}>
            <div
              data-testid="whatsapp-qr-card"
              className="bg-white border border-clay/70 p-6 flex flex-col items-center"
            >
              <div className="flex items-center gap-2 text-whatsapp mb-4">
                <FaWhatsapp className="w-5 h-5" />
                <span className="text-[11px] tracking-[0.22em] uppercase text-ink-soft">
                  {t.contact.qrWhatsapp}
                </span>
              </div>
              <div className="w-full aspect-square bg-white overflow-hidden flex items-center justify-center">
                <img
                  src={IMAGES.whatsappQR}
                  alt="WhatsApp QR — Nunu Massage"
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              </div>
              <a
                href={CONTACT.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 text-[11px] tracking-[0.22em] uppercase text-whatsapp hover:underline"
              >
                wa.me / 8618473441056
              </a>
            </div>
          </FadeUp>
          <FadeUp delay={0.3}>
            <div
              data-testid="wechat-qr-card"
              className="bg-white border border-clay/70 p-6 flex flex-col items-center"
            >
              <div className="flex items-center gap-2 text-wechat mb-4">
                <FaWeixin className="w-5 h-5" />
                <span className="text-[11px] tracking-[0.22em] uppercase text-ink-soft">
                  {t.contact.qrWechat}
                </span>
              </div>
              <div className="w-full aspect-square bg-white overflow-hidden flex items-center justify-center">
                <img
                  src={IMAGES.wechatQR}
                  alt="WeChat QR — Nunu Massage"
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              </div>
              <span className="mt-4 text-[11px] tracking-[0.22em] uppercase text-wechat">
                WeChat · 艾米
              </span>
            </div>
          </FadeUp>
        </div>
      </div>
    </div>
  </section>
);

/* ------------------------------- Footer -------------------------------- */

const Footer = ({ t }) => (
  <footer data-testid="footer" className="border-t border-clay/70 py-10 bg-sand">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[12px] text-ink-soft">
      <div className="flex items-center gap-2">
        <span className="w-1.5 h-1.5 bg-matcha rounded-full" />
        <span className="font-serif-display text-base text-ink">
          <Brand />
        </span>
        <span className="ml-3 text-ink-soft">· {t.footer.tag}</span>
      </div>
      <div className="flex items-center gap-6">
        <span>© {new Date().getFullYear()} {CONTACT.brand}. {t.footer.rights}</span>
      </div>
    </div>
  </footer>
);

/* --------------------------- Floating CTAs ----------------------------- */

const FloatingCTAs = ({ openWeChat }) => (
  <div
    data-testid="floating-ctas"
    className="fixed z-40 bottom-24 md:bottom-6 left-4 md:left-6 flex flex-col gap-3"
  >
    <a
      href={CONTACT.whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="WhatsApp"
      data-testid="float-whatsapp"
      onClick={() => trackEvent("whatsapp_click", { source: "floating_cta" })}
      className="relative w-13 h-13 md:w-14 md:h-14 bg-whatsapp text-white flex items-center justify-center rounded-full shadow-lg hover:scale-110 transition-transform duration-500"
      style={{ width: 56, height: 56 }}
    >
      <span className="pulse-ring absolute inset-0 rounded-full" />
      <FaWhatsapp className="w-6 h-6 relative" />
    </a>
    <button
      type="button"
      onClick={() => {
        trackEvent("wechat_click", { source: "floating_cta" });
        openWeChat();
      }}
      aria-label="WeChat"
      data-testid="float-wechat"
      className="w-14 h-14 bg-wechat text-white flex items-center justify-center rounded-full shadow-lg hover:scale-110 transition-transform duration-500"
    >
      <FaWeixin className="w-6 h-6" />
    </button>
  </div>
);

/* ----------------------------- WeChat Modal ---------------------------- */

const WeChatModal = ({ open, onClose, t }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT.phone);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      /* ignore */
    }
  };
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="wechat-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 30, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative bg-sand max-w-md w-full border border-clay/80 p-8 md:p-10"
          >
            <button
              type="button"
              onClick={onClose}
              data-testid="wechat-modal-close"
              className="absolute top-4 right-4 text-ink-soft hover:text-ink"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3 text-wechat mb-4">
              <FaWeixin className="w-8 h-8" />
              <span className="text-[11px] tracking-[0.28em] uppercase text-ink-soft">
                WeChat · 微信
              </span>
            </div>
            <h3 className="font-serif-display text-3xl md:text-4xl leading-tight text-ink">
              {t.wechatModal.title}
            </h3>
            <p className="mt-5 text-ink-soft text-[14px] leading-relaxed">
              {t.wechatModal.sub}
            </p>
            <div className="mt-6 bg-white border border-clay/70 p-4 flex items-center justify-center">
              <img
                src={IMAGES.wechatQR}
                alt="WeChat QR — Nunu Massage"
                className="w-full max-w-[280px] aspect-square object-contain"
                loading="lazy"
              />
            </div>
            <div className="mt-5 bg-sand-deep border border-clay/70 px-5 py-4 flex items-center justify-between">
              <span className="font-serif-display text-2xl text-ink">
                {CONTACT.phoneDisplay}
              </span>
              <button
                type="button"
                onClick={handleCopy}
                data-testid="wechat-copy-btn"
                className="flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-matcha hover:text-matcha-deep"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? t.wechatModal.copied : t.wechatModal.copy}
              </button>
            </div>
            <div className="mt-6 flex items-center gap-4">
              <a
                href={CONTACT.wechatProtocol}
                data-testid="wechat-open-app"
                className="flex-1 text-center bg-wechat text-white py-3 text-[12px] tracking-[0.22em] uppercase hover:brightness-95"
              >
                <FaWeixin className="w-4 h-4 inline mr-2" />
                Open WeChat
              </a>
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-3 text-[12px] tracking-[0.22em] uppercase text-ink-soft hover:text-ink"
              >
                {t.wechatModal.close}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* -------------------------------- App ---------------------------------- */

export default function AngleSpa() {
  const [lang, setLang] = useState(() =>
    window.localStorage.getItem("nunu_lang") === "zh" ? "zh" : "en",
  );
  const [wechatOpen, setWechatOpen] = useState(false);
  const [promoHidden, setPromoHidden] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("nunu_promo_dismissed") === "1";
  });
  const t = useMemo(() => translations[lang], [lang]);

  const handlePromoClose = () => {
    setPromoHidden(true);
    try {
      window.localStorage.setItem("nunu_promo_dismissed", "1");
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    document.documentElement.lang = lang === "zh" ? "zh-CN" : "en";
    window.localStorage.setItem("nunu_lang", lang);
  }, [lang]);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ autoRaf: true, lerp: 0.09, anchors: { offset: -110 } });
    window.lenis = lenis;
    return () => {
      lenis.destroy();
      window.lenis = null;
    };
  }, []);

  const headerOffset = promoHidden ? 0 : 40;

  return (
    <div className="relative min-h-screen bg-sand text-ink">
      <PromoBar t={t} hidden={promoHidden} onClose={handlePromoClose} />
      <Header lang={lang} setLang={setLang} t={t} offset={headerOffset} />
      <main className="relative" style={{ paddingTop: headerOffset ? `${headerOffset}px` : 0 }}>
        <Hero t={t} />
        <Marquee items={t.marquee} />
        <About t={t} />
        <Services t={t} onBook={() => {}} />
        <HowItWorks t={t} />
        <Therapist t={t} />
        <Testimonials t={t} />
        <Location t={t} />
        <FAQ t={t} />
        <Contact t={t} openWeChat={() => setWechatOpen(true)} />
      </main>
      <Footer t={t} />
      <FloatingCTAs openWeChat={() => setWechatOpen(true)} />
      <StickyMobileBar t={t} />
      <BackToTop />
      <WeChatModal open={wechatOpen} onClose={() => setWechatOpen(false)} t={t} />
    </div>
  );
}
